<template>
  <div class="plan-container">
    <!-- 查询控件卡片 -->
    <el-card class="filter-card">
      <div class="filter-container">
        <!-- 左侧查询表单 -->
        <div class="left-section">
          <el-form :inline="true" :model="filterForm" class="filter-form" size="small">
            <div class="form-content">
              <!-- 计划名称 -->
              <el-form-item>
                <el-input
                  v-model="filterForm.plan_name"
                  placeholder="计划名称"
                  clearable
                  @keyup.enter.native="handleSearch"
                  style="width: 180px">
                </el-input>
              </el-form-item>
              
              <!-- 计划内容 -->
              <el-form-item>
                <el-input
                  v-model="filterForm.plan_info"
                  placeholder="计划内容"
                  clearable
                  @keyup.enter.native="handleSearch"
                  style="width: 180px">
                </el-input>
              </el-form-item>
              
              <!-- 计划状态 -->
              <el-form-item>
                <el-select
                  v-model="filterForm.plan_status"
                  placeholder="计划状态"
                  clearable
                  style="width: 120px">
                  <el-option label="未开始" :value="0"></el-option>
                  <el-option label="进行中" :value="1"></el-option>
                  <el-option label="已完成" :value="2"></el-option>
                  <el-option label="暂停" :value="3"></el-option>
                  <el-option label="终止" :value="4"></el-option>
                </el-select>
              </el-form-item>
              
              <!-- 添加时间 -->
              <el-form-item>
                <el-date-picker
                  v-model="filterForm.add_time"
                  type="daterange"
                  range-separator="至"
                  start-placeholder="开始日期"
                  end-placeholder="结束日期"
                  value-format="yyyy-MM-dd"
                  style="width: 240px">
                </el-date-picker>
              </el-form-item>
              
              <el-form-item class="filter-buttons">
                <el-button @click="handleReset">重置</el-button>
                <el-button type="primary" @click="handleSearch">查询</el-button>
                <el-button type="success" @click="handleAdd">添加</el-button>
                <el-button type="danger" @click="handleBatchDelete" :disabled="!selectedPlans.length">删除</el-button>
              </el-form-item>
            </div>
          </el-form>
        </div>

        <!-- 右侧数据展示 -->
        <div class="right-section">
          <!-- 未开始 -->
          <div class="stat-card">
            <div class="stat-value">{{ stats.not_started || 0 }}</div>
            <div class="stat-label">未开始</div>
          </div>

          <!-- 进行中 -->
          <div class="stat-card">
            <div class="stat-value">{{ stats.in_progress || 0 }}</div>
            <div class="stat-label">进行中</div>
          </div>

          <!-- 已完成 -->
          <div class="stat-card">
            <div class="stat-value">{{ stats.completed || 0 }}</div>
            <div class="stat-label">已完成</div>
          </div>

          <!-- 总数 -->
          <div class="stat-card">
            <div class="stat-value">{{ stats.total || 0 }}</div>
            <div class="stat-label">总数</div>
          </div>
        </div>
      </div>
    </el-card>

    <!-- 数据表格卡片 -->
    <el-card class="table-card">
      <el-table
        v-loading="loading"
        :data="planList"
        stripe
        style="width: 100%"
        :header-cell-style="{
          background: '#f2f3f5',
          color: '#606266',
          fontWeight: '600',
          fontSize: '14px',
          height: '52px',
          padding: '0'
        }"
        @sort-change="handleSortChange"
        @selection-change="handleSelectionChange">
        
        <!-- 多选列 -->
        <el-table-column
          type="selection"
          width="50"
          align="center">
        </el-table-column>
        
        <!-- 序号列 -->
        <el-table-column
          type="index"
          label="序号"
          width="50"
          align="center">
          <template slot-scope="scope">
            {{ (currentPage - 1) * pageSize + scope.$index + 1 }}
          </template>
        </el-table-column>
        
        <!-- 计划名称 -->
        <el-table-column
          prop="plan_name"
          label="计划名称"
          sortable="custom"
          min-width="280">
          <template slot-scope="{row}">
            <el-link
              type="primary"
              :underline="false"
              @click="handleViewInfo(row)"
              v-if="row.plan_info">
              {{ row.plan_name }}
            </el-link>
            <span v-else>{{ row.plan_name }}</span>
          </template>
        </el-table-column>
        
        <!-- 计划来源 -->
        <el-table-column
          prop="plan_from"
          label="计划来源"
          sortable="custom"
          width="110"
          align="center">
          <template slot-scope="{row}">
            <el-tag
              :type="row.plan_from === 1 ? 'info' : 'warning'"
              size="small"
              style="height: 28px; line-height: 26px;">
              {{ row.plan_from === 1 ? '手动添加' : '复盘生成' }}
            </el-tag>
          </template>
        </el-table-column>
        
        <!-- 来源题目 -->
        <el-table-column
          prop="question_name"
          label="来源题目"
          min-width="180">
          <template slot-scope="{row}">
            <a v-if="row.question_url" 
               :href="row.question_url" 
               target="_blank" 
               class="link-text"
               @click.stop>
              {{ row.question_name }}
            </a>
            <span v-else>{{ row.question_name }}</span>
          </template>
        </el-table-column>
        
        <!-- 计划状态 -->
        <el-table-column
          prop="plan_status"
          label="计划状态"
          sortable="custom"
          width="110"
          align="center">
          <template slot-scope="{row}">
            <el-tag
              :type="getStatusType(row.plan_status)"
              size="small"
              style="height: 28px; line-height: 26px;">
              {{ getStatusText(row.plan_status) }}
            </el-tag>
          </template>
        </el-table-column>
        
        <!-- 预计开始时间 -->
        <el-table-column
          prop="scheduled_start_time"
          label="预计开始"
          sortable="custom"
          width="110">
          <template slot-scope="{row}">
            {{ formatDate(row.scheduled_start_time) }}
          </template>
        </el-table-column>
        
        <!-- 实际开始时间 -->
        <el-table-column
          prop="start_time"
          label="实际开始"
          sortable="custom"
          width="110">
          <template slot-scope="{row}">
            {{ formatDate(row.start_time) }}
          </template>
        </el-table-column>
        
        <!-- 预计完成时间 -->
        <el-table-column
          prop="scheduled_end_time"
          label="预计完成"
          sortable="custom"
          width="110">
          <template slot-scope="{row}">
            {{ formatDate(row.scheduled_end_time) }}
          </template>
        </el-table-column>
        
        <!-- 实际完成时间 -->
        <el-table-column
          prop="end_time"
          label="实际完成"
          sortable="custom"
          width="110">
          <template slot-scope="{row}">
            {{ formatDate(row.end_time) }}
          </template>
        </el-table-column>

        <!-- 耗时 -->
        <el-table-column
          prop="duration_days"
          label="耗时"
          sortable="custom"
          width="100"
          align="center">
          <template slot-scope="{row}">
            {{ formatDurationDays(row) }}
          </template>
        </el-table-column>
        
        <!-- 操作 -->
        <el-table-column
          label="操作"
          width="180"
          align="center"
          class-name="operation-column">
          <template #default="scope">
            <div class="operation-buttons">
              <el-tooltip content="编辑" placement="top" :enterable="false" :open-delay="100">
                <el-button
                  type="text"
                  class="operation-button edit-button"
                  @click="handleEdit(scope.row)">
                  <i class="el-icon-edit"></i>
                </el-button>
              </el-tooltip>
              
              <el-tooltip content="开始" placement="top" :enterable="false" :open-delay="100">
                <el-button
                  type="text"
                  class="operation-button"
                  :class="(scope.row.plan_status !== 0 && scope.row.plan_status !== 3) ? 'disabled-button' : 'start-button'"
                  :disabled="scope.row.plan_status !== 0 && scope.row.plan_status !== 3"
                  @click="handleStart(scope.row)">
                  <i class="el-icon-video-play"></i>
                </el-button>
              </el-tooltip>
              
              <el-tooltip content="暂停" placement="top" :enterable="false" :open-delay="100">
                <el-button
                  type="text"
                  class="operation-button"
                  :class="scope.row.plan_status !== 1 ? 'disabled-button' : 'pause-button'"
                  :disabled="scope.row.plan_status !== 1"
                  @click="handleUpdateStatus(scope.row.plan_code, 3)">
                  <i class="el-icon-video-pause"></i>
                </el-button>
              </el-tooltip>
              
              <el-tooltip content="完成" placement="top" :enterable="false" :open-delay="100">
                <el-button
                  type="text"
                  class="operation-button"
                  :class="scope.row.plan_status !== 1 ? 'disabled-button' : 'complete-button'"
                  :disabled="scope.row.plan_status !== 1"
                  @click="handleComplete(scope.row)">
                  <i class="el-icon-check"></i>
                </el-button>
              </el-tooltip>
              
              <el-tooltip content="终止" placement="top" :enterable="false" :open-delay="100">
                <el-button
                  type="text"
                  class="operation-button"
                  :class="scope.row.plan_status !== 1 ? 'disabled-button' : 'terminate-button'"
                  :disabled="scope.row.plan_status !== 1"
                  @click="handleTerminate(scope.row)">
                  <i class="el-icon-circle-close"></i>
                </el-button>
              </el-tooltip>
            </div>
          </template>
        </el-table-column>
      </el-table>
      
      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          background
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page.sync="currentPage"
          :page-sizes="[20, 50, 100]"
          :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total">
        </el-pagination>
      </div>
    </el-card>

    <!-- 版权信息卡片 -->
    <el-card class="copyright-card">
      <div class="copyright-content">
        <span>Copyright © 2024 OIer之路 All rights reserved.  |  闽ICP备2025095302号-1</span>
      </div>
    </el-card>

    <!-- 计划编辑对话框 -->
    <plan-dialog
      :visible.sync="editDialogVisible"
      :plan="currentPlan"
      :is-edit="!!currentPlan"
      @success="handleSuccess">
    </plan-dialog>

    <!-- 计划内容查看对话框 -->
    <plan-info-dialog
      :visible.sync="infoDialogVisible"
      :plan-info="currentPlanInfo">
    </plan-info-dialog>
  </div>
</template>

<script>
import { getPlanList, getPlanStats, deletePlan, updatePlanStatus } from '@/api/plan'
import PlanDialog from './components/PlanDialog.vue'
import PlanInfoDialog from './components/PlanInfoDialog.vue'

export default {
  name: 'Plan',
  components: {
    PlanDialog,
    PlanInfoDialog
  },
  data() {
    return {
      loading: false,
      planList: [],
      total: 0,
      currentPage: 1,
      pageSize: 20,
      sortProp: 'add_time',
      sortOrder: 'descending',
      
      filterForm: {
        plan_name: '',
        plan_info: '',
        plan_status: '',
        add_time: []
      },
      
      stats: {
        not_started: 0,    // 未开始
        in_progress: 0,    // 进行中
        completed: 0,      // 已完成
        total: 0,          // 总数
        terminated: 0      // 已终止
      },
      
      selectedPlans: [],
      
      editDialogVisible: false,
      currentPlan: null,
      
      infoDialogVisible: false,
      currentPlanInfo: ''
    }
  },
  created() {
    this.fetchData()
    this.fetchStats()
  },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        const params = {
          pageNum: this.currentPage,
          pageSize: this.pageSize,
          sort_prop: this.sortProp,
          sort_order: this.sortOrder,
          ...this.filterForm
        }
        
        // 处理时间范围
        if (this.filterForm.add_time?.length === 2) {
          params.add_time_start = this.filterForm.add_time[0]
          params.add_time_end = this.filterForm.add_time[1]
        }
        delete params.add_time
        
        const { data } = await getPlanList(params)
        this.planList = data.items
        this.total = data.total
        this.applyDurationSortIfNeeded()
      } catch (error) {
        console.error('获取计划列表失败:', error)
        this.$message.error('获取计划列表失败')
      } finally {
        this.loading = false
      }
    },
    
    async fetchStats() {
      try {
        const { data } = await getPlanStats()
        this.stats = data
      } catch (error) {
        console.error('获取统计数据失败:', error)
      }
    },
    
    handleSearch() {
      this.currentPage = 1
      this.fetchData()
    },
    
    handleReset() {
      this.filterForm = {
        plan_name: '',
        plan_info: '',
        plan_status: '',
        add_time: []
      }
      this.currentPage = 1
      this.sortProp = 'add_time'
      this.sortOrder = 'descending'
      this.fetchData()
    },
    
    handleSortChange({ prop, order }) {
      this.sortProp = prop
      this.sortOrder = order
      if (prop === 'duration_days') {
        this.applyDurationSortIfNeeded()
        return
      }
      this.fetchData()
    },
    
    handleSizeChange(val) {
      this.pageSize = val
      this.fetchData()
    },
    
    handleCurrentChange(val) {
      this.currentPage = val
      this.fetchData()
    },
    
    handleAdd() {
      this.currentPlan = null
      this.editDialogVisible = true
    },
    
    handleEdit(row) {
      this.currentPlan = row
      this.editDialogVisible = true
    },
    
    handleViewInfo(row) {
      this.currentPlanInfo = row.plan_info
      this.infoDialogVisible = true
    },
    
    handleSelectionChange(selection) {
      this.selectedPlans = selection
    },
    
    async handleBatchDelete() {
      if (this.selectedPlans.length === 0) {
        this.$message.warning('请至少选择一条记录')
        return
      }

      try {
        await this.$confirm('确认删除选中的计划吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        const planCodes = this.selectedPlans.map(plan => plan.plan_code)
        await deletePlan(planCodes)
        this.$message.success('删除成功')
        this.fetchData()
        this.fetchStats()
        this.selectedPlans = [] // 清空选择
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除失败:', error)
          this.$message.error('删除失败')
        }
      }
    },
    
    async handleStart(row) {
      try {
        await this.$confirm('确认开始该计划吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        await updatePlanStatus(row.plan_code, 1)
        this.$message.success('操作成功')
        this.fetchData()
        this.fetchStats()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('操作失败:', error)
          this.$message.error('操作失败')
        }
      }
    },
    
    async handleComplete(row) {
      try {
        await this.$confirm('确认完成该计划吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        await updatePlanStatus(row.plan_code, 2)
        this.$message.success('操作成功')
        this.fetchData()
        this.fetchStats()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('操作失败:', error)
          this.$message.error('操作失败')
        }
      }
    },
    
    async handleTerminate(row) {
      try {
        await this.$confirm('确认终止该计划吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        await updatePlanStatus(row.plan_code, 4)
        this.$message.success('操作成功')
        this.fetchData()
        this.fetchStats()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('操作失败:', error)
          this.$message.error('操作失败')
        }
      }
    },
    
    handleSuccess() {
      this.fetchData()
      this.fetchStats()
    },
    
    getStatusType(status) {
      const typeMap = {
        0: 'info',    // 未开始
        1: 'primary', // 进行中
        2: 'success', // 已完成
        3: 'warning', // 已暂停
        4: 'danger'   // 已终止
      }
      return typeMap[status] || ''
    },
    
    getStatusText(status) {
      const textMap = {
        0: '未开始',
        1: '进行中',
        2: '已完成',
        3: '已暂停',
        4: '已终止'
      }
      return textMap[status] || '未知'
    },
    
    async handleUpdateStatus(planCode, status) {
      try {
        await this.$confirm('确认更新该计划状态吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        await updatePlanStatus(planCode, status)
        this.$message.success('操作成功')
        this.fetchData()
        this.fetchStats()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('操作失败:', error)
          this.$message.error('操作失败')
        }
      }
    },
    
    // 格式化日期，只保留年月日
    formatDate(date) {
      if (!date) return '';
      // 处理默认值或无效日期
      if (date === '0000-00-00 00:00:00' || date === '0000-00-00' || date.startsWith('0000-00-00')) {
        return '';
      }
      // 如果日期包含 T，说明是标准的 ISO 格式，取前面的部分
      if (date.includes('T')) {
        return date.split('T')[0];
      }
      // 如果日期包含空格，说明是标准的 MySQL 格式，取前面的部分
      if (date.includes(' ')) {
        return date.split(' ')[0];
      }
      return date;
    },

    // 计算已完成计划的耗时（天）
    getDurationDays(row) {
      if (!row || row.plan_status !== 2) return null
      const start = this.parseDateValue(row.start_time)
      const end = this.parseDateValue(row.end_time)
      if (!start || !end || end < start) return null
      const diffMs = end.getTime() - start.getTime()
      return diffMs / (24 * 60 * 60 * 1000)
    },

    // 格式化耗时展示文本
    formatDurationDays(row) {
      const durationDays = this.getDurationDays(row)
      if (durationDays === null) return '--'
      return `${Math.ceil(durationDays)}天`
    },

    // 解析日期字符串（兼容 ISO 与 MySQL 格式）
    parseDateValue(date) {
      if (!date || date === '0000-00-00 00:00:00' || date === '0000-00-00' || date.startsWith('0000-00-00')) {
        return null
      }
      const parsed = new Date(date.replace(' ', 'T'))
      return Number.isNaN(parsed.getTime()) ? null : parsed
    },

    // 对当前页数据按耗时进行前端排序
    applyDurationSortIfNeeded() {
      if (this.sortProp !== 'duration_days' || !this.sortOrder) return
      const multiplier = this.sortOrder === 'ascending' ? 1 : -1
      const sortedList = [...this.planList].sort((a, b) => {
        const aDuration = this.getDurationDays(a)
        const bDuration = this.getDurationDays(b)
        const aValue = aDuration === null ? Number.NEGATIVE_INFINITY : Math.ceil(aDuration)
        const bValue = bDuration === null ? Number.NEGATIVE_INFINITY : Math.ceil(bDuration)
        if (aValue === bValue) return 0
        return (aValue - bValue) * multiplier
      })
      this.planList = sortedList
    }
  }
}
</script>

<style lang="scss" scoped>
.plan-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 0;
  margin: 0;
  background-color: #f0f2f5;
  overflow: hidden;
  
  .filter-card {
    margin: 8px 0 0 0;
    flex-shrink: 0;
    background-color: #fff;
    position: relative;
    z-index: 10;
    height: 72px;
    
    :deep(.el-card__body) {
      height: 100%;
      padding: 0;
    }
  }
  
  .filter-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 100%;
    padding: 0 16px;

    .left-section {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-right: 20px;
      flex-shrink: 1;
      overflow-x: auto;
      height: 100%;
      
      .filter-form {
        display: flex;
        align-items: center;
        white-space: nowrap;
        height: 100%;
        
        .form-content {
          display: flex;
          align-items: center;
          gap: 8px;
          height: 100%;
          
          :deep(.el-input),
          :deep(.el-select),
          :deep(.el-date-picker) {
            .el-input__inner {
              height: 32px;
              line-height: 32px;
              font-size: 13px;
            }
          }
          
          :deep(.el-button) {
            height: 32px;
            line-height: 30px;
            padding: 0 16px;
            font-size: 13px;
          }
          
          .el-form-item {
            margin-bottom: 0;
            margin-right: 0;
            display: flex;
            align-items: center;
          }

          .filter-buttons {
            margin-left: auto;
            white-space: nowrap;
            
            .el-button + .el-button {
              margin-left: 8px;
            }
          }
        }
      }
    }

    .right-section {
      display: flex;
      align-items: center;
      gap: 16px;
      flex-shrink: 0;
      padding: 0 12px;
      background-color: #f6f8fa;
      border-radius: 8px;
      height: 52px;
      
      .stat-card {
        text-align: center;
        min-width: 60px;
        flex-shrink: 0;
        display: flex;
        flex-direction: column;
        justify-content: center;
        position: relative;
        padding: 0 10px;

        &:not(:last-child)::after {
          content: '';
          position: absolute;
          right: -8px;
          top: 50%;
          transform: translateY(-50%);
          height: 36px;
          width: 1px;
          background-color: #dcdfe6;
        }

        .stat-value {
          font-size: 18px;
          font-weight: 600;
          color: #1f2937;
          line-height: 1;
          margin-bottom: 4px;
        }

        .stat-label {
          font-size: 12px;
          color: #6b7280;
          line-height: 1;
        }
      }
    }
  }
  
  .table-card {
    margin: 8px 0 0 0;
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background-color: #fff;
    border-radius: 4px;
    min-height: 0;
    
    :deep(.el-card__body) {
      height: 100%;
      padding: 0;
      display: flex;
      flex-direction: column;
      min-height: 0;
    }
    
    :deep(.el-table) {
      flex: 1;
      height: 100%;
      
      .el-table__header-wrapper {
        position: sticky;
        top: 0;
        z-index: 2;
        background-color: #f5f7fa;

        th {
          background-color: #f5f7fa;
          color: #606266;
          font-weight: 600;
          font-size: 14px;
          height: 52px;
          padding: 0;
          border-bottom: 1px solid #ebeef5;
        }
      }
      
      .el-table__body-wrapper {
        overflow-y: auto;
        height: calc(100% - 60px);
      }

      // 表格行高度
      .el-table__row {
        height: 52px;
      }

      // 表格单元格内边距
      td {
        padding: 8px 0;
        border-bottom: 1px solid #ebeef5;
      }

      // 操作按钮样式
      .operation-buttons {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 8px;
        
        .operation-button {
          width: 24px;
          height: 24px;
          padding: 0;
          border-radius: 4px;
          margin: 0;
          border: none;
          transition: all 0.2s ease;
          
          i {
            font-size: 14px;
            color: white;
          }
          
          // 编辑按钮 - 深蓝色
          &.edit-button {
            background-color: #0056b3;
            
            &:hover {
              background-color: #004494;
              transform: translateY(-1px);
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            
            &:active {
              transform: translateY(0);
              background-color: #003d82;
            }
          }
          
          // 开始按钮 - 青绿色
          &.start-button {
            background-color: #20c997;
            
            &:hover {
              background-color: #1ca085;
              transform: translateY(-1px);
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            
            &:active {
              transform: translateY(0);
              background-color: #17a673;
            }
          }
          
          // 暂停按钮 - 橙色
          &.pause-button {
            background-color: #E6A23C;
            
            &:hover {
              background-color: #d49134;
              transform: translateY(-1px);
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            
            &:active {
              transform: translateY(0);
              background-color: #c08025;
            }
          }
          
          // 完成按钮 - 绿色
          &.complete-button {
            background-color: #67C23A;
            
            &:hover {
              background-color: #5daf34;
              transform: translateY(-1px);
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            
            &:active {
              transform: translateY(0);
              background-color: #529b2e;
            }
          }
          
          // 终止按钮 - 红色
          &.terminate-button {
            background-color: #F56C6C;
            
            &:hover {
              background-color: #dd5858;
              transform: translateY(-1px);
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            
            &:active {
              transform: translateY(0);
              background-color: #c54848;
            }
          }
          
          // 禁用状态按钮
          &.disabled-button {
            background-color: #F2F3F5;
            cursor: not-allowed;
            opacity: 0.6;
            
            i {
              color: #C0C4CC;
            }
            
            &:hover {
              background-color: #F2F3F5;
              transform: none;
              box-shadow: none;
            }
          }
          
          &:disabled {
            cursor: not-allowed;
            background-color: #F2F3F5;
            opacity: 0.6;
            
            i {
              color: #C0C4CC;
            }
            
            &:hover {
              transform: none;
              box-shadow: none;
            }
          }
        }
      }

      // 调整操作列宽度
      .el-table__header th.operation-column,
      .el-table__body td.operation-column {
        width: 170px !important;
        min-width: 170px !important;
      }
    }
  }
  
  .pagination-container {
    height: 40px;
    padding: 0 16px;
    background-color: #fff;
    border-top: 1px solid #e4e7ed;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    flex-shrink: 0;
  }
  
  .copyright-card {
    margin: 8px 0;
    flex-shrink: 0;
    background-color: #fff;
    border-radius: 4px;
    height: 40px;
    position: relative;
    z-index: 1;
    
    :deep(.el-card__body) {
      height: 100%;
      padding: 0;
    }
    
    .copyright-content {
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #909399;
      font-size: 14px;
      
      span {
        line-height: 1;
      }
    }
  }
}

/* 操作列背景样式 */
::v-deep .operation-column .cell {
  background-color: rgba(248, 249, 250, 0.6) !important;
  padding: 8px 4px !important;
}

/* 链接样式 */
.link-text {
  color: #409EFF;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
}
</style> 